import { TodoItem } from './adhd-todo-manager';

export interface XPReward {
  amount: number;
  reason: string;
  multipliers: Array<{
    factor: number;
    description: string;
  }>;
}

export class XPRewardCalculator {
  
  /**
   * Calculate XP reward for completing a task
   */
  calculateTaskCompletionXP(task: TodoItem): XPReward {
    let baseXP = 10; // Base reward for any completed task
    const multipliers: Array<{ factor: number; description: string }> = [];
    
    // Priority multipliers
    switch (task.priority) {
      case 'urgent':
        multipliers.push({ factor: 2.5, description: 'Urgent priority' });
        break;
      case 'high':
        multipliers.push({ factor: 2.0, description: 'High priority' });
        break;
      case 'medium':
        multipliers.push({ factor: 1.5, description: 'Medium priority' });
        break;
      case 'low':
        multipliers.push({ factor: 1.0, description: 'Low priority' });
        break;
    }
    
    // Time-based multipliers
    if (task.estimated_minutes) {
      if (task.estimated_minutes >= 120) { // 2+ hours
        multipliers.push({ factor: 2.0, description: 'Long task (2+ hours)' });
      } else if (task.estimated_minutes >= 60) { // 1+ hours
        multipliers.push({ factor: 1.5, description: 'Medium task (1+ hour)' });
      } else if (task.estimated_minutes >= 30) { // 30+ minutes
        multipliers.push({ factor: 1.2, description: 'Focused task (30+ min)' });
      }
    }
    
    // Due date multipliers
    if (task.due_date) {
      const dueDate = new Date(task.due_date);
      const now = new Date();
      const hoursUntilDue = (dueDate.getTime() - now.getTime()) / (1000 * 60 * 60);
      
      if (hoursUntilDue < 0) {
        // Task was overdue
        const hoursOverdue = Math.abs(hoursUntilDue);
        if (hoursOverdue > 168) { // Over a week overdue
          multipliers.push({ factor: 3.0, description: 'Completed severely overdue task' });
        } else if (hoursOverdue > 24) { // Over a day overdue
          multipliers.push({ factor: 2.5, description: 'Completed overdue task' });
        } else {
          multipliers.push({ factor: 2.0, description: 'Completed late task' });
        }
      } else if (hoursUntilDue < 24) {
        // Task due within 24 hours
        multipliers.push({ factor: 1.8, description: 'Completed urgent deadline' });
      } else if (hoursUntilDue < 72) {
        // Task due within 3 days
        multipliers.push({ factor: 1.4, description: 'Completed ahead of deadline' });
      }
    }
    
    // Eisenhower Matrix multipliers
    switch (task.eisenhower_quadrant) {
      case 1: // Do First (Important & Urgent)
        multipliers.push({ factor: 2.5, description: 'Important & Urgent task' });
        break;
      case 2: // Schedule (Important & Not Urgent)
        multipliers.push({ factor: 2.0, description: 'Important task' });
        break;
      case 3: // Delegate (Urgent & Not Important)
        multipliers.push({ factor: 1.5, description: 'Urgent task' });
        break;
      case 4: // Delete (Not Important & Not Urgent)
        multipliers.push({ factor: 1.0, description: 'Completed low-impact task' });
        break;
    }
    
    // ADHD-specific multipliers
    if (task.adhd_energy_level === 'low' && task.status === 'completed') {
      multipliers.push({ factor: 1.8, description: 'Completed low-energy task' });
    }
    
    if (task.adhd_context_cost && task.adhd_context_cost >= 7) {
      multipliers.push({ factor: 1.6, description: 'High context-switching task' });
    }
    
    if (task.adhd_hyperfocus_compatible) {
      multipliers.push({ factor: 1.3, description: 'Hyperfocus-compatible task' });
    }
    
    // Apply all multipliers
    const totalMultiplier = multipliers.reduce((total, mult) => total * mult.factor, 1);
    const finalXP = Math.round(baseXP * totalMultiplier);
    
    return {
      amount: finalXP,
      reason: `Completed "${task.title}"`,
      multipliers
    };
  }
  
  /**
   * Calculate XP for focus session completion
   */
  calculateFocusSessionXP(durationMinutes: number, wasInterrupted = false): XPReward {
    let baseXP = Math.round(durationMinutes * 0.5); // 0.5 XP per minute
    const multipliers: Array<{ factor: number; description: string }> = [];
    
    // Duration bonuses
    if (durationMinutes >= 90) {
      multipliers.push({ factor: 2.0, description: 'Extended focus session (90+ min)' });
    } else if (durationMinutes >= 60) {
      multipliers.push({ factor: 1.5, description: 'Long focus session (60+ min)' });
    } else if (durationMinutes >= 25) {
      multipliers.push({ factor: 1.2, description: 'Pomodoro session completed' });
    }
    
    // Penalty for interruptions
    if (wasInterrupted) {
      multipliers.push({ factor: 0.8, description: 'Session was interrupted' });
    } else {
      multipliers.push({ factor: 1.1, description: 'Uninterrupted focus session' });
    }
    
    const totalMultiplier = multipliers.reduce((total, mult) => total * mult.factor, 1);
    const finalXP = Math.round(baseXP * totalMultiplier);
    
    return {
      amount: finalXP,
      reason: `Completed ${durationMinutes} minute focus session`,
      multipliers
    };
  }
  
  /**
   * Calculate XP for streak maintenance
   */
  calculateStreakXP(streakDays: number): XPReward {
    const multipliers: Array<{ factor: number; description: string }> = [];
    let baseXP = 5 * streakDays; // Base XP increases with streak length
    
    // Milestone bonuses
    if (streakDays >= 100) {
      multipliers.push({ factor: 5.0, description: '100+ day streak milestone!' });
    } else if (streakDays >= 50) {
      multipliers.push({ factor: 3.0, description: '50+ day streak milestone!' });
    } else if (streakDays >= 30) {
      multipliers.push({ factor: 2.5, description: '30+ day streak milestone!' });
    } else if (streakDays >= 14) {
      multipliers.push({ factor: 2.0, description: 'Two week streak!' });
    } else if (streakDays >= 7) {
      multipliers.push({ factor: 1.5, description: 'One week streak!' });
    } else if (streakDays >= 3) {
      multipliers.push({ factor: 1.2, description: 'Building momentum!' });
    }
    
    const totalMultiplier = multipliers.reduce((total, mult) => total * mult.factor, 1);
    const finalXP = Math.round(baseXP * totalMultiplier);
    
    return {
      amount: finalXP,
      reason: `${streakDays} day productivity streak`,
      multipliers
    };
  }
  
  /**
   * Calculate XP for taking breaks (important for ADHD brain health)
   */
  calculateBreakXP(durationMinutes: number): XPReward {
    const multipliers: Array<{ factor: number; description: string }> = [];
    let baseXP = Math.round(durationMinutes * 0.2); // Lower base XP than focus sessions
    
    // Break duration rewards
    if (durationMinutes >= 15) {
      multipliers.push({ factor: 1.5, description: 'Long break for proper rest' });
    } else if (durationMinutes >= 5) {
      multipliers.push({ factor: 1.2, description: 'Short break for mental reset' });
    }
    
    // Encourage regular breaks for ADHD management
    multipliers.push({ factor: 1.1, description: 'Self-care: taking needed breaks' });
    
    const totalMultiplier = multipliers.reduce((total, mult) => total * mult.factor, 1);
    const finalXP = Math.round(baseXP * totalMultiplier);
    
    return {
      amount: Math.max(finalXP, 3), // Minimum 3 XP for taking any break
      reason: `Took a ${durationMinutes} minute break`,
      multipliers
    };
  }

  /**
   * Calculate bonus XP for completing daily goals
   */
  calculateDailyGoalXP(completedTasks: number, totalPlannedTasks: number): XPReward {
    const multipliers: Array<{ factor: number; description: string }> = [];
    const completionRate = completedTasks / Math.max(totalPlannedTasks, 1);
    let baseXP = completedTasks * 5; // 5 XP per completed task
    
    // Completion rate bonuses
    if (completionRate >= 1.0) {
      multipliers.push({ factor: 3.0, description: 'Perfect day - all tasks completed!' });
    } else if (completionRate >= 0.8) {
      multipliers.push({ factor: 2.0, description: 'Excellent day - 80%+ completion' });
    } else if (completionRate >= 0.6) {
      multipliers.push({ factor: 1.5, description: 'Good day - 60%+ completion' });
    } else if (completionRate >= 0.4) {
      multipliers.push({ factor: 1.2, description: 'Progress made - 40%+ completion' });
    }
    
    // Task quantity bonuses
    if (completedTasks >= 10) {
      multipliers.push({ factor: 1.5, description: 'High productivity day (10+ tasks)' });
    } else if (completedTasks >= 5) {
      multipliers.push({ factor: 1.2, description: 'Productive day (5+ tasks)' });
    }
    
    const totalMultiplier = multipliers.reduce((total, mult) => total * mult.factor, 1);
    const finalXP = Math.round(baseXP * totalMultiplier);
    
    return {
      amount: finalXP,
      reason: `Daily goal: ${completedTasks}/${totalPlannedTasks} tasks completed`,
      multipliers
    };
  }
}